/* eslint-disable */
import React, { FC, useEffect, useRef } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Col, Row, Spin, Tag, Typography } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { Stage } from '~/types/Application';
import { Checkbox, DocumentInput } from '~/components';
import { useGetApplication, useGetPayloadWithProvided, useSubmitStep } from '~/controllers/wizard';
import { useGetSignatoriesInfo } from '~/controllers/contract';
import { usePostAgentUpdatedFields } from '~/controllers/agentReview';
import { KsaAuthorizedSignatory } from '~/types/ksa/KsaAuthorizedSignatory';
import { Draft } from '~/types/Draft';
import Form from '~/components/Form';
import WizardForm from '~/components/WizardForm';
import { useHistory } from 'react-router';
import longdash from '~/constants/longdash';
import { toAgentContractFileNames } from '~/utils/agentActivityLogFormatters';
import dayjs from 'dayjs';
import { DateTimeFormat } from '~/constants/dateTime';
import { useDirection } from '~/hooks/i18nHooks';
import { DirectionProp } from '~/types/Direction';
import AgreementPricingForm from './components/AgreementPricingForm';

const AgentContractView: FC = () => {
  const { t } = useTranslation('ksaContract');
  const dir = useDirection();
  const payload = useGetPayloadWithProvided();
  const [form] = Form.useForm();
  const submitMutation = useSubmitStep();
  const history = useHistory();
  const applicationQuery = useGetApplication();
  const isKsaReviewStage = applicationQuery?.data?.stage === Stage.ksaReview;
  const isKsaContract = applicationQuery?.data?.stage === Stage.ksaContract;

  const selectedAuthorisedSignatory = payload?.authorizedSignatory as
    | KsaAuthorizedSignatory
    | undefined;

  const { isLoading: signatoriesIsLoading } = useGetSignatoriesInfo(isKsaContract);

  useEffect(() => {
    if (isKsaReviewStage) {
      submitMutation.mutateAsync({ contractReview: { reviewedByAgent: false } });
      history.push('/ksa/review/completeReview');
    }
  }, [isKsaReviewStage]);

  // ── Applicant Details — from payload.applicantDetails ──────────────────────
  const applicantDetails: any = (payload as any)?.applicantDetails;
  const applicantName: string | undefined = applicantDetails?.name;
  const applicantMobile: string | undefined = applicantDetails?.phoneNumber;
  const applicantNationalId: string | undefined = applicantDetails?.nationalId;

  // ── Agreement Details ────────────────────────────────────────────────────────
  // Contract Version Number — not yet available from backend
  const contractVersionNumber: string | undefined = undefined;

  // Agreement Acceptance Date & Time — from payload.agreementAcceptance.acceptedAt
  const acceptanceDateTime: string | undefined =
    (payload as any)?.agreementAcceptance?.acceptedAt;

  // Nafath Verification Date & Time — from payload.nafathAuthentication.verifiedAt
  const nafathVerificationDateTime: string | undefined =
    (payload as any)?.nafathAuthentication?.verifiedAt;

  const formatDateTime = (value?: string) => {
    if (!value) return undefined;
    return dayjs(value).isValid() ? dayjs(value).format(DateTimeFormat.DATE_TIME) : value;
  };

  /** Small helper — shows the value or a long-dash placeholder */
  const displayValue = (value?: string) => value ?? longdash;

  // ── Agent-Updated-Fields API ─────────────────────────────────────────────────
  const agentUpdatedFieldsMutation = usePostAgentUpdatedFields();
  const applicationUid = applicationQuery?.data?.uid;
  // Tracks the previous form snapshot so we can send accurate oldData
  const prevValuesRef = useRef<Record<string, any>>({});

  const postAgentUpdate = (fieldName: string, oldValue: any, newValue: any) => {
    if (!applicationUid || isEqual(oldValue, newValue)) return;
    agentUpdatedFieldsMutation.mutate({
      applicationId: applicationUid,
      stepKey: 'review.contractReview',
      updatedFields: [{ oldData: { [fieldName]: oldValue }, newData: { [fieldName]: newValue } }],
    });
  };

  /**
   * Fires on every form value change (checkbox toggle, file upload/remove).
   * changedValues only contains the keys that changed.
   */
  const handleValuesChange = (changedValues: any, allValues: any) => {
    const prev = prevValuesRef.current;
    const cr = changedValues?.contractReview;
    if (!cr) return;

    if ('reviewedByAgent' in cr) {
      postAgentUpdate(
        'reviewedByAgent',
        prev?.contractReview?.reviewedByAgent ?? false,
        cr.reviewedByAgent,
      );
    }

    if ('agentContract' in cr) {
      const oldNames = toAgentContractFileNames(prev?.contractReview?.agentContract);
      const newNames = toAgentContractFileNames(allValues?.contractReview?.agentContract);
      postAgentUpdate('agentContract', oldNames, newNames);
    }

    // Always keep the snapshot up to date
    prevValuesRef.current = allValues;
  };

  return !isKsaReviewStage ? (
    <Container dir={dir}>
      <WizardForm<Draft<'contractReview'>>
        stage={Stage.ksaReview}
        payloadKey="contractReview"
        form={form}
        disabled={false}
        onValuesChange={handleValuesChange}
      >
        <Typography.Title>{t('Agreement')}</Typography.Title>

        {/* ── ROW 1: Signatory Details + Applicant Details ── */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <InfoCard>
              <CardTitle>{t('Signatory Details')}</CardTitle>

              <FieldBlock>
                <FieldLabel>{t('Signatory Name')}</FieldLabel>
                <FieldValue>{displayValue(selectedAuthorisedSignatory?.fullName)}</FieldValue>
              </FieldBlock>

              <Divider />

              <FieldBlock>
                <FieldLabel>{t('Signatory National ID')}</FieldLabel>
                <FieldValue>{displayValue(selectedAuthorisedSignatory?.nationalId)}</FieldValue>
              </FieldBlock>

              <Divider />

              <FieldBlock>
                <FieldLabel>{t('Signatory IP Address')}</FieldLabel>
                <FieldValue>{displayValue(selectedAuthorisedSignatory?.ipAddress)}</FieldValue>
              </FieldBlock>
            </InfoCard>
          </Col>

          <Col xs={24} sm={12}>
            <InfoCard>
              <CardTitle>{t('Applicant Details')}</CardTitle>

              <FieldBlock>
                <FieldLabel>{t('Applicant Name')}</FieldLabel>
                <FieldValue>{displayValue(applicantName)}</FieldValue>
              </FieldBlock>

              <Divider />

              <FieldBlock>
                <FieldLabel>{t('Applicant Mobile Number')}</FieldLabel>
                <FieldValue>{displayValue(applicantMobile)}</FieldValue>
              </FieldBlock>

              <Divider />

              <FieldBlock>
                <FieldLabel>{t('Applicant National ID')}</FieldLabel>
                <FieldValue>{displayValue(applicantNationalId)}</FieldValue>
              </FieldBlock>
            </InfoCard>
          </Col>
        </Row>

        {/* ── ROW 2: Agreement Details (full width) ── */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <InfoCard>
              <CardTitle>{t('Agreement Details')}</CardTitle>

              {/* Signed Contract — full width */}
              <FieldLabel style={{ marginBottom: 8 }}>{t('Signed Contract')}</FieldLabel>
              {payload?.contract?.length ? (
                <ReadOnlyDocumentWrap>
                  <DocumentInput
                    name=""
                    maxCount={payload.contract.length}
                    value={payload.contract}
                    noRemove
                    multiple={false}
                  />
                </ReadOnlyDocumentWrap>
              ) : (
                <NoContractRow>
                  <FilePdfOutlined style={{ fontSize: 24, color: '#8c8c8c' }} />
                  <Typography.Text type="secondary">{t('File Name')}</Typography.Text>
                </NoContractRow>
              )}

              <Divider style={{ margin: '12px 0' }} />

              <FieldBlock>
                <FieldLabel>{t('Contract Version Number')}</FieldLabel>
                <FieldValueRow>
                  <FieldValue>{displayValue(contractVersionNumber)}</FieldValue>
                  {/* {!contractVersionNumber && (
                    <NewTag color="blue">{t('New', { ns: 'common' })}</NewTag>
                  )} */}
                </FieldValueRow>
              </FieldBlock>

              <Divider />

              <FieldBlock>
                <FieldLabel>{t('Agreement Acceptance Date & Time')}</FieldLabel>
                <FieldValue>{displayValue(formatDateTime(acceptanceDateTime))}</FieldValue>
              </FieldBlock>

              <Divider />

              <FieldBlock>
                <FieldLabel>{t('Nafath Verification Date & Time')}</FieldLabel>
                <FieldValue>{displayValue(formatDateTime(nafathVerificationDateTime))}</FieldValue>
              </FieldBlock>

              <Divider />

              {/* Contract Reviewed by Agent — checkbox */}
              <CheckboxRow>
                <WizardForm.Item
                  name={['contractReview', 'reviewedByAgent']}
                  valuePropName="checked"
                  initialValue={false}
                  className="ReviewItemCheckbox"
                  disabled={false}
                >
                  <Checkbox />
                </WizardForm.Item>
                <Typography.Text strong>{t('Contract Reviewed by Agent')}</Typography.Text>
              </CheckboxRow>

              {/* Agent-uploaded contract file */}
              <FieldLabel style={{ marginTop: 12, marginBottom: 8 }}>
                {t('Upload File')}
              </FieldLabel>
              <WizardForm.Item
                name={['contractReview', 'agentContract']}
                className="ReviewItemCheckbox"
              >
                <DocumentInput
                  name=""
                  multiple={false}
                />
              </WizardForm.Item>
            </InfoCard>

            {<AgreementPricingForm />}
          </Col>
        </Row>
      </WizardForm>
    </Container>
  ) : (
    <>
      <Spin />
    </>
  );
};

/* ─── Styled components ─────────────────────────────────────── */

const Container = styled.div<DirectionProp>``;

const FieldBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
`;

const InfoCard = styled.div`
  background: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 16px;
`;

const CardTitle = styled(Typography.Text)`
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #595959;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const FieldValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldLabel = styled(Typography.Text)`
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  flex-shrink: 0;
`;

const FieldValue = styled(Typography.Text)`
  font-size: 13px;
  color: #595959;
`;

const Divider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 4px 0;
`;

const NewTag = styled(Tag)`
  font-size: 11px;
  padding: 0 6px;
  border-radius: 4px;
  flex-shrink: 0;
`;

const NoContractRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;

  .ReviewItemCheckbox {
    margin-bottom: 0 !important;
  }
`;

const ReadOnlyDocumentWrap = styled.div`
  .afterUpload-delete-icon {
    display: none !important;
  }
`;

export default AgentContractView;
